import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Form, Field } from 'react-final-form';
import { Button, ButtonTypes } from '../../../common/Button';
import { Loader } from '../../App/components/Loader';
import { joinClassroom } from '../../../firebase/classrooms';
import { showToast } from '../actions/toast';
import { closeClassroomModal } from '../actions/ide';
import { setCurrentClassroom } from '../../User/actions';

/**
 * Join Classroom (any signed-in user). Enter a join code → joinClassroom callable. Staff join as
 * instructors, everyone else as students (decided server-side). Shows a spinner while awaiting and a
 * result/error toast. The code field is prefilled after the signup→login pending-join round-trip.
 */
const JoinClassroomModal = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const prefill = useSelector((state) => state.ide.classroomJoinPrefill);

  function validate({ joincode }) {
    const errors = {};
    if (!joincode || joincode.trim().length === 0) {
      errors.joincode = t('JoinClassroomModal.EnterCode', {
        defaultValue: 'Enter a join code.'
      });
    }
    return errors;
  }

  async function onSubmit({ joincode }) {
    let submitError;
    try {
      const result = await joinClassroom(joincode.trim());
      // Joining sets the user's current classroom (spec: student, and instructors who join too).
      dispatch(setCurrentClassroom(result.classroomId, result.name));
      dispatch(
        showToast(
          result.joinedAs === 'instructor'
            ? 'Toast.ClassroomJoinedInstructor'
            : 'Toast.ClassroomJoinedStudent',
          4000
        )
      );
      dispatch(closeClassroomModal());
    } catch (e) {
      // Firebase callable errors surface as `functions/<code>`; map the ones we throw server-side.
      const code = (e && e.code) || '';
      if (code.includes('failed-precondition')) {
        dispatch(showToast('Toast.ClassroomJoinExpired', 4000));
      } else if (code.includes('not-found')) {
        dispatch(showToast('Toast.ClassroomJoinInvalid', 4000));
      } else if (code.includes('resource-exhausted')) {
        dispatch(showToast('Toast.ClassroomFull', 4000));
      } else {
        dispatch(showToast('Toast.ClassroomJoinError', 4000));
      }
      // Keep the modal open so the user can correct the code.
      submitError = {
        joincode: t('JoinClassroomModal.TryAgain', {
          defaultValue: 'Could not join. Check the code and try again.'
        })
      };
    }
    return submitError;
  }

  return (
    <div className="classroom-modal">
      <Form
        validate={validate}
        onSubmit={onSubmit}
        initialValues={{ joincode: prefill || '' }}
      >
        {({ handleSubmit, submitting, touched, errors }) =>
          submitting ? (
            <div style={{ minHeight: '6rem' }}>
              <Loader />
            </div>
          ) : (
            <form className="classroom-form" onSubmit={handleSubmit}>
              <Field name="joincode">
                {(field) => (
                  <React.Fragment>
                    <label htmlFor="classroom-joincode">
                      {t('JoinClassroomModal.CodeLabel', {
                        defaultValue: 'Join code'
                      })}
                    </label>
                    <input
                      id="classroom-joincode"
                      type="text"
                      maxLength="6"
                      autoComplete="off"
                      autoCapitalize="characters"
                      // eslint-disable-next-line jsx-a11y/no-autofocus
                      autoFocus
                      placeholder={t('JoinClassroomModal.CodePlaceholder', {
                        defaultValue: 'ABC234'
                      })}
                      {...field.input}
                    />
                  </React.Fragment>
                )}
              </Field>
              <Button type={ButtonTypes.SUBMIT} disabled={submitting}>
                {t('JoinClassroomModal.Submit', { defaultValue: 'Join' })}
              </Button>
              {touched.joincode && errors.joincode && (
                <span className="form-error" aria-live="polite">
                  {errors.joincode}
                </span>
              )}
            </form>
          )
        }
      </Form>
    </div>
  );
};

export default JoinClassroomModal;
